import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CreditCard,
  Zap,
  Send,
  Users,
  Package,
  ArrowUpRight,
  ShieldCheck,
  Wallet as WalletIcon,
  Activity,
  TrendingUp,
  Plus,
} from "lucide-react";
import { useMagneticCursor } from "@/lib/useMagneticCursor";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABEL, RAIL_LABEL, formatINR } from "@/lib/constants";
import { toast } from "sonner";

const QUICK_ACTIONS = [
  { label: "Credit Card Bill", icon: CreditCard, to: "/bbps", primary: true, meta: "Primary flow" },
  { label: "Electricity", icon: Zap, to: "/bbps", meta: "BBPS" },
  { label: "Payout", icon: Send, to: "/payouts", meta: "UPI · Bank" },
  { label: "Beneficiaries", icon: Users, to: "/beneficiaries", meta: "Manage" },
];

const STATUS_STYLE = {
  success: "bg-success text-success-foreground",
  failed: "bg-destructive text-destructive-foreground",
};

export default function Dashboard() {
  const { user } = useAuth();
  const kycVerified = user?.kyc_status === "verified";
  const magnetic = useMagneticCursor();

  const [balance, setBalance] = useState(user?.wallet_balance || 0);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heldCredits, setHeldCredits] = useState(null);

  useEffect(() => {
    if (!kycVerified) { setLoading(false); return; }
    (async () => {
      try {
        const [b, s, t, h] = await Promise.all([
          api.get("/wallet/balance"),
          api.get("/transactions/summary?period=month"),
          api.get("/transactions?limit=5"),
          api.get("/admin/payment-links/partner/held-credits").catch(() => ({ data: null })),
        ]);
        setBalance(b.data.balance);
        setSummary(s.data);
        setRecent(t.data.items || []);
        setHeldCredits(h.data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    })();
  }, [kycVerified]);

  const successRate = summary && summary.total_transactions > 0
    ? Math.round((summary.successful_transactions / summary.total_transactions) * 100)
    : 0;

  const KPIS = [
    { label: "Wallet balance", value: formatINR(balance), hint: "Top up to increase limits", icon: WalletIcon, accent: "primary" },
    { label: "Transactions (MTD)", value: summary?.total_transactions ?? 0, hint: `${successRate}% success`, icon: Activity, accent: "secondary" },
    { label: "Volume (MTD)", value: formatINR(summary?.total_volume || 0), hint: "Across BBPS + payouts", icon: TrendingUp, accent: "primary" },
    { label: "Earnings (MTD)", value: formatINR(summary?.total_earnings || 0), hint: "Margin − platform fee", icon: TrendingUp, accent: "secondary" },
  ];

  return (
    <DashboardLayout>
      <div className="animate-fade-up">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="overline">Overview</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              Hello, {user?.full_name?.split(" ")[0] || "Partner"}.
            </h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Everything's live. Credit card bill payments, utilities and payouts at your
              fingertips.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              data-testid="header-kyc-badge"
              variant={kycVerified ? "default" : "outline"}
              className={kycVerified ? "bg-success text-success-foreground hover:bg-success" : "border-primary/30 text-primary"}
            >
              <span className="mr-1 h-1.5 w-1.5 rounded-full inline-block bg-current opacity-80" />
              KYC · {user?.kyc_status}
            </Badge>
            {kycVerified && (
              <Button asChild size="sm" variant="outline" data-testid="quick-topup" className="magnetic-btn" onMouseMove={magnetic}>
                <Link to="/wallet"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />Top-up</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Held credits banner — only render when partner has wallet_topup credits awaiting paymode approval */}
        {heldCredits && heldCredits.count > 0 && (
          <div
            data-testid="held-credits-banner"
            className="mt-8 surface-card border-amber-200 bg-amber-50/70 p-5 flex items-start gap-4"
          >
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <WalletIcon className="h-5 w-5 text-amber-700" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="overline text-amber-800">Awaiting approval</span>
                {heldCredits.rejected_count > 0 && (
                  <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700 text-[10px]">
                    {heldCredits.rejected_count} rejected
                  </Badge>
                )}
              </div>
              <h3 className="font-display text-lg font-semibold text-amber-900 mt-1">
                {formatINR(heldCredits.total_amount)} from {heldCredits.count} wallet top-up{heldCredits.count > 1 ? "s" : ""} pending admin paymode approval
              </h3>
              <p className="text-sm text-amber-800/80 mt-1">
                Your payment landed successfully, but the payment mode used (card / UPI handle / bank channel) is new on your account.
                Admin will release the credit to your wallet once they approve the mode. You'll see the balance update automatically.
              </p>
            </div>
            <Button asChild size="sm" variant="outline" data-testid="held-credits-view-btn" className="border-amber-400 text-amber-900 hover:bg-amber-100">
              <Link to="/payment-links">View links</Link>
            </Button>
          </div>
        )}

        {/* KPI row */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {KPIS.map((k) => {
            const Icon = k.icon;
            const accent = k.accent === "secondary" ? "bg-secondary/10 text-secondary" : "bg-accent text-primary";
            return (
              <div
                key={k.label}
                data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group surface-card p-6 interactive-card relative overflow-hidden"
              >
                <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary/5 transition-all duration-500 group-hover:scale-125 group-hover:bg-primary/10" />
                <div className="relative flex items-start justify-between">
                  <span className="overline">{k.label}</span>
                  <div className={`h-8 w-8 rounded-md grid place-items-center ${accent}`}>
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="relative font-mono text-3xl mt-3 tabular-nums">{loading ? "…" : k.value}</div>
                <div className="relative text-xs text-muted-foreground mt-2">{k.hint}</div>
              </div>
            );
          })}
        </div>

        {/* KYC callout */}
        {!kycVerified && (
          <div data-testid="kyc-callout" className="mt-8 surface-card p-6 flex items-start gap-4 border-primary/30 relative overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-50 pointer-events-none" />
            <div className="relative h-10 w-10 rounded-md bg-primary text-primary-foreground grid place-items-center shrink-0">
              <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div className="relative flex-1">
              <div className="font-semibold">
                {user?.kyc_status === "submitted" ? "KYC under review" : "Complete your KYC to unlock transactions"}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {user?.kyc_status === "submitted"
                  ? "Our team typically completes verification within 24 hours."
                  : "Submit business & identity details so BBPS and payout rails can be enabled."}
              </p>
            </div>
            <Button asChild data-testid="go-to-kyc" className="relative magnetic-btn group" onMouseMove={magnetic}>
              <Link to="/kyc">
                {user?.kyc_status === "submitted" ? "View submission" : "Start KYC"}
                <ArrowUpRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
            </Button>
          </div>
        )}

        {/* Quick actions */}
        {kycVerified && (
          <div className="mt-10">
            <div className="flex items-end justify-between">
              <div>
                <span className="overline">Quick actions</span>
                <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Start a transaction</h2>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {QUICK_ACTIONS.map(({ label, icon: Icon, to, primary, meta }) => (
                <Link
                  to={to}
                  key={label}
                  data-testid={`quick-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                  className={[
                    "group text-left surface-card p-5 interactive-card relative overflow-hidden",
                    primary ? "ring-2 ring-secondary" : "",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between">
                    <div className={[
                      "h-10 w-10 rounded-md grid place-items-center transition-all",
                      primary ? "bg-secondary text-secondary-foreground" : "bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground",
                    ].join(" ")}>
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <span className="overline">{meta}</span>
                  </div>
                  <div className="font-semibold mt-4">{label}</div>
                  <ArrowUpRight className="mt-3 h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" strokeWidth={1.5} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Recent transactions */}
        {kycVerified && (
          <div className="mt-10 surface-card overflow-hidden">
            <div className="p-6 pb-4 flex items-center justify-between">
              <div>
                <span className="overline">Activity</span>
                <h3 className="font-display text-xl font-bold tracking-tight mt-1">Recent transactions</h3>
              </div>
              <Button asChild variant="outline" size="sm" data-testid="view-all-txns">
                <Link to="/transactions">View all</Link>
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Earnings</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : recent.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No transactions yet. <Link to="/bbps" className="text-primary hover:underline">Start a BBPS payment</Link>.
                  </TableCell></TableRow>
                ) : recent.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                      {new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="uppercase font-mono text-[10px]">{t.type}</Badge></TableCell>
                    <TableCell>{CATEGORY_LABEL[t.category] || RAIL_LABEL[t.category] || t.category}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{formatINR(t.amount)}</TableCell>
                    <TableCell className={`text-right font-mono tabular-nums ${t.net_earnings > 0 ? "text-success" : "text-destructive"}`}>
                      {t.net_earnings >= 0 ? "+" : ""}{formatINR(t.net_earnings)}
                    </TableCell>
                    <TableCell><Badge className={STATUS_STYLE[t.status] || ""}>{t.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
